import { useState } from 'react'
import { useLocale } from '../../App'

interface ActionBarProps {
  isUserTurn: boolean
  currentBet: number
  userBet: number
  userChips: number
  callAmount: number
  minRaise: number
  onAction: (action: string, amount?: number) => void
  loading: boolean
}

export function ActionBar({
  isUserTurn,
  currentBet,
  userBet,
  userChips,
  callAmount,
  minRaise,
  onAction,
  loading,
}: ActionBarProps) {
  const { t } = useLocale()
  const bigBlind = 20
  const [raiseAmount, setRaiseAmount] = useState(minRaise)
  const [showRaiseInput, setShowRaiseInput] = useState(false)
  const [betAmount, setBetAmount] = useState(bigBlind)
  const [showBetInput, setShowBetInput] = useState(false)

  if (!isUserTurn) {
    return (
      <div className="flex justify-center py-4">
        <div className="flex items-center gap-2 rounded-full bg-slate-800/60 px-6 py-2.5 backdrop-blur-sm border border-slate-700/50">
          <svg className="h-4 w-4 animate-spin text-blue-400" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm font-medium text-slate-300">{t('app.waiting')}</span>
        </div>
      </div>
    )
  }

  const canCheck = currentBet === 0 || userBet === currentBet
  const canCall = currentBet > userBet && userChips >= callAmount
  const canBet = currentBet === 0 && userChips >= bigBlind
  const canRaise = currentBet > 0 && userChips > callAmount && minRaise <= userChips + userBet
  const canAllIn = userChips > 0

  const handleBet = () => {
    if (showBetInput) {
      onAction('bet', betAmount)
      setShowBetInput(false)
    } else {
      setShowBetInput(true)
      setBetAmount(bigBlind)
    }
  }

  const handleRaise = () => {
    if (showRaiseInput) {
      onAction('raise', raiseAmount)
      setShowRaiseInput(false)
    } else {
      setShowRaiseInput(true)
      setRaiseAmount(minRaise)
    }
  }

  return (
    <div className="relative flex flex-col items-center px-4 pt-2 pb-4">
      {/* Bet/Raise slider - overlay above buttons */}
      {(showBetInput || showRaiseInput) && (
        <div className="absolute bottom-full left-0 right-0 mx-auto mb-2 w-full max-w-xs z-10">
          {showBetInput && (
            <div className="flex items-center gap-3 rounded-xl bg-slate-800/95 px-4 py-3 border border-slate-700/50 shadow-xl backdrop-blur-sm">
              <span className="text-xs font-semibold text-slate-400 shrink-0">{t('app.amount')}</span>
              <input
                type="range"
                min={bigBlind}
                max={userChips}
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-bold text-white tabular-nums w-16 text-right">
                {betAmount.toLocaleString()}
              </span>
            </div>
          )}
          {showRaiseInput && (
            <div className="flex items-center gap-3 rounded-xl bg-slate-800/95 px-4 py-3 border border-slate-700/50 shadow-xl backdrop-blur-sm">
              <span className="text-xs font-semibold text-slate-400 shrink-0">{t('app.amount')}</span>
              <input
                type="range"
                min={minRaise}
                max={userChips + userBet}
                value={raiseAmount}
                onChange={(e) => setRaiseAmount(Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-bold text-white tabular-nums w-16 text-right">
                {raiseAmount.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap justify-center gap-1.5">
        {/* Fold */}
        <button
          onClick={() => onAction('fold')}
          disabled={loading}
          className="min-w-[60px] rounded-lg px-2 py-1.5 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-40 shadow-md"
          style={{ background: 'linear-gradient(135deg, #ef4444, #b91c1c)' }}
        >
          {t('app.fold')}
        </button>

        {/* Check */}
        {canCheck && (
          <button
            onClick={() => onAction('check')}
            disabled={loading}
            className="min-w-[60px] rounded-lg px-2 py-1.5 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-40 shadow-md"
            style={{ background: 'linear-gradient(135deg, #60a5fa, #2563eb)' }}
          >
            {t('app.check')}
          </button>
        )}

        {/* Call */}
        {canCall && (
          <button
            onClick={() => onAction('call')}
            disabled={loading}
            className="min-w-[60px] rounded-lg px-2 py-1.5 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-40 shadow-md"
            style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}
          >
            📞 {callAmount.toLocaleString()}
          </button>
        )}

        {/* Bet */}
        {canBet && (
          <button
            onClick={handleBet}
            disabled={loading}
            className="min-w-[60px] rounded-lg px-2 py-1.5 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-40 shadow-md"
            style={{
              background: showBetInput
                ? 'linear-gradient(135deg, #22c55e, #15803d)'
                : 'linear-gradient(135deg, #34d399, #059669)',
            }}
          >
            {showBetInput ? t('app.confirm') : t('app.bet')}
          </button>
        )}

        {/* Raise */}
        {canRaise && (
          <button
            onClick={handleRaise}
            disabled={loading}
            className="min-w-[60px] rounded-lg px-2 py-1.5 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-40 shadow-md"
            style={{
              background: showRaiseInput
                ? 'linear-gradient(135deg, #22c55e, #15803d)'
                : 'linear-gradient(135deg, #34d399, #059669)',
            }}
          >
            {showRaiseInput ? t('app.confirm') : t('app.raise')}
          </button>
        )}

        {/* All-in */}
        {canAllIn && (
          <button
            onClick={() => onAction('allin')}
            disabled={loading}
            className="min-w-[60px] rounded-lg px-2 py-1.5 text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-40 shadow-md animate-pulse-glow"
            style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
          >
            {t('app.allin')}
          </button>
        )}
      </div>
    </div>
  )
}
